import { Extension, type Extensions, Node as TiptapNode, mergeAttributes } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Highlight } from '@tiptap/extension-highlight';
import { Image } from '@tiptap/extension-image';
import { TextAlign } from '@tiptap/extension-text-align';
import { Color, TextStyle } from '@tiptap/extension-text-style';
import { Youtube, isValidYoutubeUrl } from '@tiptap/extension-youtube';
import {
  AnnouncementAlignment,
  AnnouncementMediaNode,
  AnnouncementMediaSize,
} from '@/app/modules/announcements/core/_models';
import { isAnnouncementMediaUrl, readMediaAttribute } from '@/app/modules/announcements/core/_utils';

/** A origem provisória da mídia que o editor ainda está enviando. */
export const ANNOUNCEMENT_UPLOAD_SLOT_PREFIX = 'upload:';

const MEDIA_CLASS_NAME = 'd-block mw-100 h-auto mb-4';

// O Tiptap não tem nó de vídeo: só o player nativo, sem autoplay, e com origem http ou https (ou a do envio).
const AnnouncementVideo = TiptapNode.create({
  name: AnnouncementMediaNode.VIDEO,
  group: 'block',
  atom: true,
  draggable: true,
  addAttributes: () => ({ src: { default: null } }),
  parseHTML: () => [
    {
      tag: 'video[src]',
      getAttrs: (element) => {
        const source = element.getAttribute('src') ?? '';
        const isUploading = source.startsWith(ANNOUNCEMENT_UPLOAD_SLOT_PREFIX);
        const isAccepted = isAnnouncementMediaUrl(source) || isUploading;
        return isAccepted ? null : false;
      },
    },
  ],
  renderHTML: ({ HTMLAttributes }) => [
    'video',
    mergeAttributes(HTMLAttributes, {
      class: MEDIA_CLASS_NAME,
      controls: 'true',
      playsinline: 'true',
      preload: 'metadata',
    }),
  ],
});

// Só o iframe do YouTube entra, pelo domínio sem cookies: outro iframe colado some ao exibir.
const AnnouncementYoutube = Youtube.extend({
  parseHTML: () => [
    {
      tag: 'div[data-youtube-video] iframe',
      getAttrs: (element) => {
        const source = element.getAttribute('src') ?? '';
        return isValidYoutubeUrl(source) ? null : false;
      },
    },
  ],
}).configure({ nocookie: true, rel: 0 });

/** Os nós de mídia que aceitam alinhamento e largura. */
const MEDIA_NODES: AnnouncementMediaNode[] = Object.values(AnnouncementMediaNode);

// O atributo vem do HTML salvo, como texto: só passa o valor que o enum conhece.
const MEDIA_ALIGNMENTS = new Set<string>([AnnouncementAlignment.CENTER, AnnouncementAlignment.RIGHT]);
const MEDIA_SIZE_VALUES: string[] = Object.values(AnnouncementMediaSize);
const MEDIA_SIZES = new Set<string>(MEDIA_SIZE_VALUES);

// À esquerda e no tamanho natural não gravam nada; a leitura descarta valor fora da lista.
const AnnouncementMediaLayout = Extension.create({
  name: 'announcementMediaLayout',
  addGlobalAttributes: () => [
    {
      types: MEDIA_NODES,
      attributes: {
        align: {
          default: null,
          parseHTML: (element) => readMediaAttribute(element, 'data-align', MEDIA_ALIGNMENTS),
          renderHTML: (attributes) => (attributes.align ? { 'data-align': attributes.align as string } : {}),
        },
        size: {
          default: null,
          parseHTML: (element) => readMediaAttribute(element, 'data-size', MEDIA_SIZES),
          renderHTML: (attributes) => (attributes.size ? { 'data-size': attributes.size as string } : {}),
        },
      },
    },
  ],
});

/** O esquema que o editor escreve e a leitura aceita: o que não estiver aqui some ao exibir. */
const TEXT_ALIGNMENTS = Object.values(AnnouncementAlignment);

export const ANNOUNCEMENT_BODY_EXTENSIONS: Extensions = [
  StarterKit.configure({
    heading: { levels: [2, 3] },
    horizontalRule: false,
    link: { openOnClick: false, autolink: true, defaultProtocol: 'https' },
  }),
  TextStyle,
  Color,
  Highlight.configure({ multicolor: true }),
  TextAlign.configure({ types: ['heading', 'paragraph'], alignments: TEXT_ALIGNMENTS }),
  Image.configure({ allowBase64: false, HTMLAttributes: { class: MEDIA_CLASS_NAME } }),
  AnnouncementVideo,
  AnnouncementYoutube,
  AnnouncementMediaLayout,
];

/** A lista de recebidos: visto, ciente e dispensado mudam a situação dela. */
export const RECEIVED_ANNOUNCEMENTS_TABLE_KEY = 'announcement-received-list';
